import { env } from '@/utils/env';
import { storage } from '@/utils/storage';

const pendingRequests = new Map<string, Promise<Response> | null>();
const cache = new Map<string, any>();

export async function deduppableRequest(
  url: string,
  options?: RequestInit & { dedupe: boolean },
) {
  const shouldDedupe = options?.dedupe !== false;
  if (shouldDedupe) {
    const pendingRequest = pendingRequests.get(url);
    if (pendingRequest) return await pendingRequest;
  }

  const requestPromise = fetch(url, options as any);
  if (shouldDedupe) pendingRequests.set(url, requestPromise);
  const response = await requestPromise;
  if (shouldDedupe) pendingRequests.set(url, null);

  return response;
}

const defaultHeaders = {
  'Content-Type': 'application/json',
};

const API_BASE = 'api';

export function setAuth(token: string | null) {
  if (token == null) {
    storage.local.token.remove();
  } else {
    storage.local.token.set(token);
  }
}

export function isLoggedIn() {
  const auth = storage.local.token.get();
  return auth != null;
}

export function logout() {
  storage.remove(['mobile', 'address', 'user', 'token', 'cart']);
  setAuth(null);
}

export type Options = Omit<RequestInit, 'headers'> & {
  params?: Partial<Record<string, string | number | boolean>>;
  headers?: RequestInit['headers'] | Record<string, string | undefined>;
  memCache?: boolean | { expiresIn: number /* ms */ };
};
const request = (base: string, options: RequestInit) =>
  async function <Data = any>(
    url: string,
    userOptions: Options = {},
  ): Promise<{
    status: number;
    headers: Headers;
    body: Data;
  }> {
    let fastifyServer = null;
    if (env.isSSR()) {
      fastifyServer = globalThis.server;
    }
    const currentOptions: RequestInit = {
      ...options,
      ...userOptions,
      headers: JSON.parse(
        JSON.stringify({
          ...options.headers,
          ...userOptions.headers,
        }),
      ),
    };

    const auth = storage.local.token.get();
    if (auth && currentOptions.headers != null) {
      // @ts-ignore
      currentOptions.headers['Authorization'] = `Bearer ${auth}`;
    }

    const urlBase =
      fastifyServer == null
        ? `${window.location.protocol}//${window.location.hostname}:${window.location.port}`
        : `http://server`;

    const currentURL = new URL(`${base}${url}`, urlBase);
    Object.entries(userOptions.params || {}).forEach(([key, value]) => {
      if (typeof value === 'boolean' || value) {
        currentURL.searchParams.set(key, value.toString());
      }
    });

    if (fastifyServer == null && userOptions.memCache) {
      const key = currentURL.toString();
      const slice = cache.get(key);
      if (slice) {
        // force cache
        if (typeof userOptions.memCache === 'boolean') {
          return slice;
        }
        const expiresAt = slice.createdAt + userOptions.memCache.expiresIn;
        const expired = expiresAt < Date.now();
        if (!expired) {
          return slice;
        }
        cache.delete(key);
      }
    }

    let responseBody;
    let response: Response;

    if (fastifyServer == null) {
      response = await fetch(currentURL, currentOptions);

      if (response.status === 401) {
        logout();
        throw new Error('Unauthorized!');
      }
    } else {
      let urlString = currentURL.pathname;
      if (currentURL.search) {
        urlString += currentURL.search;
      }
      const headers = { ...currentOptions.headers };
      // TODO: save authorization in cookies too so we can fill it here.
      // we are deleting authorization to make all ssr request as guest users.
      delete headers['Authorization'];
      // @ts-ignore
      const fastifyResponse = await fastifyServer.inject({
        url: urlString,
        headers,
      });
      response = new Response(fastifyResponse.body, {
        headers: fastifyResponse.headers,
        status: fastifyResponse.statusCode,
        statusText: fastifyResponse.statusMessage,
      });
    }

    if (
      response?.headers
        .get('content-type')
        ?.toLowerCase()
        .includes('application/json')
    ) {
      responseBody = await response.json();
    } else {
      responseBody = await response.text();
    }

    const res = {
      status: response.status,
      headers: response.headers,
      body: responseBody as Data,
    };

    if (!response.ok) {
      const error = {
        ...res,
        message: responseBody.message || 'network error!',
      };
      throw error;
    }

    if (fastifyServer == null && userOptions.memCache) {
      cache.set(
        currentURL.toString(),
        Object.assign(res, {
          createdAt: Date.now(),
        }),
      );
    }

    return res;
  };

type RequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
const generateRestful = <Method extends RequestMethod>(
  base: string,
  methods: Method[],
  headers: RequestInit['headers'] = defaultHeaders,
) =>
  methods.reduce(
    (acc, curr) =>
      Object.assign(acc, {
        [curr.toLowerCase()]: request(base, {
          headers,
          method: curr,
        }),
      }),
    {} as Record<Lowercase<Method>, ReturnType<typeof request>>,
  );

export default {
  base: generateRestful(String(API_BASE), [
    'GET',
    'POST',
    'PUT',
    'DELETE',
    'PATCH',
  ]),
};
